/**
 * Bundle Offers & Checkout Upsell
 * Replaces third-party bundle app with native theme functionality.
 *
 * Part 1: BundleOffers – checkbox list on product page. Intercepts the
 *         fetch call to /cart/add and converts the request from single-item
 *         to multi-item format, adding bundle items alongside the main product.
 * Part 2: CheckoutUpsell – popup that intercepts checkout and offers
 *         a last-chance discount on items not yet in cart.
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------ */

  function fetchCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }
    }).then(function (r) { return r.json(); });
  }

  function addItemsToCart(items, sectionIds) {
    var body = { items: items };
    if (sectionIds && sectionIds.length) {
      body.sections = sectionIds;
      body.sections_url = window.location.pathname;
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  function applyDiscount(code) {
    if (!code) return Promise.resolve();
    return fetch('/discount/' + encodeURIComponent(code), {
      credentials: 'same-origin'
    });
  }

  function getCartVariantIds(cart) {
    return cart.items.map(function (i) { return i.variant_id; });
  }

  /* ------------------------------------------------------------------
     Part 1: Bundle Offers (Product Page)
     Strategy: Intercept window.fetch to modify /cart/add requests.
     When bundle items are checked, we convert the single-item FormData
     (id + quantity) into multi-item format (items[0][id], items[0][quantity], ...)
     This works WITH product-form.js instead of fighting against it.
     ------------------------------------------------------------------ */

  var bundleInitialized = false;

  function initBundleOffers() {
    if (bundleInitialized) return;

    var container = document.querySelector('.bundle-offers');
    if (!container) return;

    bundleInitialized = true;

    var items = container.querySelectorAll('.bundle-offers__item');
    var discountCode = container.dataset.discountCode;

    var savingsSummary = container.querySelector('.bundle-offers__savings');

    function updateBundleSavings() {
      if (!savingsSummary) return;
      var checked = container.querySelectorAll('input[type="checkbox"]:checked');
      if (!checked.length) {
        savingsSummary.style.display = 'none';
        return;
      }
      var totalCents = 0;
      checked.forEach(function (cb) {
        var li = cb.closest('.bundle-offers__item');
        if (li && li.dataset.saveAmount) {
          totalCents += parseInt(li.dataset.saveAmount, 10);
        }
      });
      var formatted = (totalCents / 100).toFixed(2);
      savingsSummary.textContent = "You're saving $" + formatted + ' with this bundle!';
      savingsSummary.style.display = '';
    }

    // Toggle checked state on item click
    items.forEach(function (item) {
      var checkbox = item.querySelector('input[type="checkbox"]');

      item.addEventListener('click', function (e) {
        if (e.target.tagName === 'INPUT') return;
        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      });

      checkbox.addEventListener('change', function () {
        item.classList.toggle('is-checked', checkbox.checked);

        // Magnetic snap animation
        item.classList.remove('is-snap');
        void item.offsetWidth;
        item.classList.add('is-snap');

        updateBundleSavings();
      });

      if (checkbox.checked) {
        item.classList.add('is-checked');
      }
    });

    // Initial savings calculation
    updateBundleSavings();

    // Per-category accordion toggles
    var toggleBtns = container.querySelectorAll('[data-bundle-toggle]');
    toggleBtns.forEach(function(btn) {
      var category = btn.closest('.bundle-offers__category');
      var itemsList = category ? category.querySelector('.bundle-offers__category-items') : null;
      if (!itemsList) return;

      // Auto-expand categories marked with data-expanded="true"
      if (category && category.getAttribute('data-expanded') === 'true') {
        btn.classList.add('is-open');
        itemsList.classList.add('is-open');
      }

      btn.addEventListener('click', function() {
        var isOpen = btn.classList.contains('is-open');
        btn.classList.toggle('is-open', !isOpen);
        itemsList.classList.toggle('is-open', !isOpen);
      });
    });

    // Note: Discount codes removed — use Shopify automatic discounts instead
    // so that bundle (20%) and checkout upsell (30%) discounts can stack.

    // Patch fetch to inject bundle items into /cart/add requests
    patchFetchForBundles(container);
  }

  function getCheckedBundleVariantIds(container) {
    var checked = container.querySelectorAll('input[type="checkbox"]:checked');
    var ids = [];
    checked.forEach(function (cb) {
      ids.push(cb.value);
    });
    return ids;
  }

  function patchFetchForBundles(bundleContainer) {
    var originalFetch = window.fetch;

    window.fetch = function (url, options) {
      // Only intercept /cart/add requests with FormData body
      var urlStr = (url && url.toString) ? url.toString() : '';
      var isCartAdd = urlStr.indexOf('/cart/add') !== -1;

      if (isCartAdd && options && options.body instanceof FormData) {
        var bundleIds = getCheckedBundleVariantIds(bundleContainer);

        if (bundleIds.length > 0) {
          var formData = options.body;
          var mainId = formData.get('id');
          var mainQty = formData.get('quantity') || '1';

          // Preserve sections data
          var sections = formData.get('sections');
          var sectionsUrl = formData.get('sections_url');

          // Remove single-item fields — Shopify ignores items[] when
          // standalone id/quantity are present in multipart/form-data
          formData.delete('id');
          formData.delete('quantity');

          // Add bundle items FIRST so they appear below the main product in cart
          for (var i = 0; i < bundleIds.length; i++) {
            formData.append('items[' + i + '][id]', bundleIds[i]);
            formData.append('items[' + i + '][quantity]', '1');
            formData.append('items[' + i + '][properties][_source]', 'product_page');
          }

          // Add main product LAST so it appears at the top of the cart
          var mainIdx = bundleIds.length;
          formData.append('items[' + mainIdx + '][id]', mainId);
          formData.append('items[' + mainIdx + '][quantity]', mainQty);
        }
      }

      return originalFetch.apply(this, arguments);
    };
  }

  /* ------------------------------------------------------------------
     Part 2: Checkout Upsell Popup
     ------------------------------------------------------------------ */

  function initCheckoutUpsell() {
    var overlay = document.getElementById('checkout-upsell-overlay');
    if (!overlay) {
      console.log('[Checkout Upsell] No overlay element found');
      return;
    }

    var closeBtn = overlay.querySelector('.checkout-upsell__close');
    var skipBtn = overlay.querySelector('.checkout-upsell__skip');
    var addBtn = overlay.querySelector('.checkout-upsell__add-btn');
    var addBtnText = addBtn ? addBtn.querySelector('.checkout-upsell__add-btn-text') : null;
    var listEl = overlay.querySelector('.checkout-upsell__list');

    console.log('[Checkout Upsell] Initialized, items found:', overlay.querySelectorAll('.checkout-upsell__item').length);

    function closePopup() {
      overlay.classList.remove('is-active');
      document.body.style.overflow = '';
    }

    if (closeBtn) closeBtn.addEventListener('click', closePopup);
    if (skipBtn) {
      skipBtn.addEventListener('click', function () {
        closePopup();
        proceedToCheckout();
      });
    }

    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) {
        closePopup();
        proceedToCheckout();
      }
    });

    var savingsHook = overlay.querySelector('.checkout-upsell__savings-hook');
    var defaultBtnText = addBtn ? (addBtn.dataset.defaultText || 'Add items to cart') : 'Add items to cart';

    function updateFooterState() {
      // Dynamic button text
      var checked = overlay.querySelectorAll('.checkout-upsell__item input[type="checkbox"]:checked');
      var count = checked.length;

      if (addBtnText) {
        if (count === 0) {
          addBtnText.textContent = defaultBtnText;
        } else if (count === 1) {
          addBtnText.textContent = 'Add 1 item & save extra';
        } else {
          addBtnText.textContent = 'Add ' + count + ' items & save extra';
        }
      }

      // Savings hook
      if (savingsHook) {
        if (count === 0) {
          savingsHook.style.display = 'none';
        } else {
          var totalSaveCents = 0;
          checked.forEach(function (cb) {
            var li = cb.closest('.checkout-upsell__item');
            if (li && li.dataset.saveAmount) {
              totalSaveCents += parseInt(li.dataset.saveAmount, 10);
            }
          });
          var formatted = (totalSaveCents / 100).toFixed(2);
          savingsHook.textContent = "You'll save an extra $" + formatted + '! Keep the streak going.';
          savingsHook.style.display = '';
        }
      }
    }

    if (listEl) {
      listEl.addEventListener('click', function (e) {
        var item = e.target.closest('.checkout-upsell__item');
        if (!item) return;
        var cb = item.querySelector('input[type="checkbox"]');
        if (!cb) return;
        if (e.target.tagName !== 'INPUT') {
          cb.checked = !cb.checked;
        }
        item.classList.toggle('is-checked', cb.checked);

        // Micro-interaction: pulse the selected item
        item.classList.remove('is-pulse');
        void item.offsetWidth; // force reflow
        item.classList.add('is-pulse');

        // Micro-interaction: pulse the CTA button to draw attention
        if (addBtn && cb.checked) {
          addBtn.classList.remove('is-pulse');
          void addBtn.offsetWidth;
          addBtn.classList.add('is-pulse');
        }

        // Update dynamic footer
        updateFooterState();
      });
    }

    if (addBtn) {
      addBtn.addEventListener('click', function () {
        var checked = overlay.querySelectorAll('.checkout-upsell__item input[type="checkbox"]:checked');
        if (!checked.length) {
          // Shake the button and show a hint
          addBtn.classList.remove('is-shake');
          void addBtn.offsetWidth;
          addBtn.classList.add('is-shake');

          if (savingsHook) {
            savingsHook.textContent = 'Select at least one item to add!';
            savingsHook.style.display = '';
            savingsHook.classList.add('is-hint');
            setTimeout(function () {
              savingsHook.classList.remove('is-hint');
              if (!overlay.querySelectorAll('.checkout-upsell__item input[type="checkbox"]:checked').length) {
                savingsHook.style.display = 'none';
              }
            }, 2500);
          }
          return;
        }

        var items = [];
        checked.forEach(function (cb) {
          items.push({
            id: parseInt(cb.value, 10),
            quantity: 1,
            properties: { '_source': 'checkout_popup' }
          });
        });

        addBtn.disabled = true;
        if (addBtnText) addBtnText.textContent = 'Adding...';

        addItemsToCart(items)
          .then(function () {
            closePopup();
            proceedToCheckout();
          })
          .catch(function (err) {
            console.error('Checkout upsell error:', err);
            closePopup();
            proceedToCheckout();
          })
          .finally(function () {
            addBtn.disabled = false;
            if (addBtnText) addBtnText.textContent = defaultBtnText;
          });
      });
    }

    // Intercept checkout buttons — use BOTH click and submit handlers
    // to ensure we catch the checkout action regardless of how it's triggered
    function handleCheckoutIntercept(e) {
      // Already showing popup, don't intercept again
      if (overlay.classList.contains('is-active')) return;

      // Query items dynamically in case they weren't rendered at init time
      var itemEls = overlay.querySelectorAll('.checkout-upsell__item');
      if (!itemEls.length) {
        console.log('[Checkout Upsell] No upsell items in popup, skipping');
        return;
      }

      console.log('[Checkout Upsell] Intercepted checkout, checking cart...');

      e.preventDefault();
      e.stopImmediatePropagation();

      fetchCart().then(function (cart) {
        var inCart = getCartVariantIds(cart);
        console.log('[Checkout Upsell] Cart variant IDs:', inCart);
        var availableItems = [];

        itemEls.forEach(function (item) {
          var cb = item.querySelector('input[type="checkbox"]');
          if (!cb) return;
          var variantId = parseInt(cb.value, 10);
          console.log('[Checkout Upsell] Checking variant', variantId, 'in cart:', inCart.indexOf(variantId) !== -1);

          if (inCart.indexOf(variantId) === -1) {
            item.style.display = '';
            cb.checked = false;
            item.classList.remove('is-checked');
            availableItems.push(item);
          } else {
            item.style.display = 'none';
          }
        });

        if (availableItems.length === 0) {
          console.log('[Checkout Upsell] All upsell items already in cart, proceeding to checkout');
          proceedToCheckout();
          return;
        }

        console.log('[Checkout Upsell] Showing popup with', availableItems.length, 'items');
        // Reset footer state for fresh popup
        updateFooterState();
        overlay.classList.add('is-active');
        document.body.style.overflow = 'hidden';
      }).catch(function (err) {
        console.error('[Checkout Upsell] Cart fetch error:', err);
        proceedToCheckout();
      });
    }

    // Capture-phase click handler for checkout buttons
    document.addEventListener('click', function (e) {
      var target = e.target.closest('#CartDrawer-Checkout, .cart__checkout-button, [name="checkout"], button[form="CartDrawer-Form"][name="checkout"]');
      if (!target) return;
      console.log('[Checkout Upsell] Checkout button clicked:', target.id || target.className);
      handleCheckoutIntercept(e);
    }, true);

    // Also capture form submit as a backup — the checkout button submits CartDrawer-Form
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form) return;
      // Check if this is the cart form being submitted for checkout
      var isCartForm = form.id === 'CartDrawer-Form' || form.id === 'cart' ||
                       (form.action && form.action.indexOf('/cart') !== -1 && form.method === 'post');
      if (!isCartForm) return;

      // Check if the submit was triggered by a checkout button (not an update button)
      var submitter = e.submitter;
      var isCheckout = submitter && (submitter.name === 'checkout' || submitter.id === 'CartDrawer-Checkout');
      if (!isCheckout) return;

      console.log('[Checkout Upsell] Form submit intercepted for checkout');
      handleCheckoutIntercept(e);
    }, true);
  }

  function proceedToCheckout() {
    var cartForm = document.getElementById('CartDrawer-Form');
    if (cartForm) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'checkout';
      input.value = '';
      cartForm.appendChild(input);
      cartForm.submit();
      return;
    }
    window.location.href = '/checkout';
  }

  /* ------------------------------------------------------------------
     Initialize with MutationObserver for template injection timing
     ------------------------------------------------------------------ */

  function waitForBundleAndInit() {
    initCheckoutUpsell();

    // Try immediately
    if (document.querySelector('.bundle-offers')) {
      initBundleOffers();
      return;
    }

    // The bundle section uses a <template> that injects on DOMContentLoaded.
    // Watch for the bundle container to appear in the DOM.
    var observer = new MutationObserver(function () {
      if (document.querySelector('.bundle-offers')) {
        observer.disconnect();
        initBundleOffers();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Safety: stop watching after 10 seconds
    setTimeout(function () {
      observer.disconnect();
      initBundleOffers();
    }, 10000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForBundleAndInit);
  } else {
    waitForBundleAndInit();
  }

})();
